import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { projectsService, AvailableChannel } from '../../services/projects.service'

interface Props {
  projectId: number
  onClose: () => void
  onAdded: () => void
}

export default function ChannelPickerModal({ projectId, onClose, onAdded }: Props) {
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')

  const { data: channels = [], isLoading } = useQuery({
    queryKey: ['availableChannels', projectId],
    queryFn: () => projectsService.getAvailableChannels(projectId),
  })

  const filtered = useMemo(() => {
    if (!search) return channels
    const q = search.toLowerCase()
    return channels.filter(
      c => c.title.toLowerCase().includes(q) || (c.username || '').toLowerCase().includes(q),
    )
  }, [channels, search])

  const addMutation = useMutation({
    mutationFn: () => projectsService.bulkAddByIds(projectId, Array.from(selected)),
    onSuccess: (r) => {
      toast.success(`Добавлено: ${r.added}`)
      onAdded()
      onClose()
    },
    onError: () => toast.error('Ошибка добавления'),
  })

  const toggle = (id: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(filtered.map(c => c.id)))
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[85vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold text-lg">Выбрать каналы</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>

        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Поиск по названию или @handle"
          className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
        />

        <div className="flex items-center justify-between mb-2 text-sm">
          <span className="text-gray-500">
            {isLoading ? 'Загрузка...' : `Доступно: ${filtered.length}`}
          </span>
          {filtered.length > 0 && (
            <button onClick={selectAll} className="text-primary-600 hover:underline">
              {selected.size === filtered.length ? 'Снять все' : 'Выбрать все'}
            </button>
          )}
        </div>

        <div className="flex-1 overflow-auto border rounded-lg">
          {filtered.length > 0 ? (
            filtered.map((ch: AvailableChannel) => (
              <label
                key={ch.id}
                className={`flex items-center gap-3 p-3 border-b last:border-b-0 cursor-pointer hover:bg-gray-50 ${
                  selected.has(ch.id) ? 'bg-primary-50' : ''
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected.has(ch.id)}
                  onChange={() => toggle(ch.id)}
                  className="h-4 w-4 text-primary-600 rounded border-gray-300"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 truncate">{ch.title}</span>
                    {ch.username && <span className="text-xs text-gray-400">@{ch.username}</span>}
                    <span className="text-xs text-gray-400">
                      {ch.channel_type === 'group' ? 'Группа' : 'Канал'}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                    {ch.members_count != null && (
                      <span>{ch.members_count.toLocaleString()} подп.</span>
                    )}
                    {ch.description && (
                      <span className="truncate">{ch.description.slice(0, 80)}</span>
                    )}
                  </div>
                </div>
              </label>
            ))
          ) : (
            <div className="p-8 text-center text-gray-400 text-sm">
              {isLoading
                ? 'Загрузка...'
                : channels.length === 0
                  ? 'Все ваши каналы уже в проекте. Добавьте новые в разделе «Каналы».'
                  : 'Ничего не найдено по запросу'}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t">
          <span className="text-sm text-gray-600">Выбрано: {selected.size}</span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border text-gray-700 text-sm rounded-lg hover:bg-gray-50"
            >
              Отмена
            </button>
            <button
              onClick={() => addMutation.mutate()}
              disabled={selected.size === 0 || addMutation.isPending}
              className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              Добавить в проект
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
